import { t } from "../../hooks/useAppTranslation";
import React, { createRef, useEffect, useRef, useState, useMemo } from "react";
import { IProject } from "../../Redux/Reducers/projects";
import TableCustomLazyLoading from "../TableCustom/TableCustomLazyLoading";
import { useAppDispatch } from "../../hooks/hooks";
import { getPagedProjects } from "../../Redux/Actions/Projects";
import { store } from "../../Redux/Store/Store";
import {Waiter} from '.././WaitingSign/Waiting';

type Props = {
  handleProject(value?: IProject): void;
  handleClosePopup(): void;
};
interface ISorting {
  column: string;
  order: string;
}

interface IFilter {
  projectNumber?: string;
  beneficiaryName?: string;
  beneficiaryIdentityNumber?: string;
  title?: string;
}

interface IPaging {
  page: number;
  pageSize: number;
}

export const ProjectTable: React.FC<Props> = ({
  handleProject,
  handleClosePopup,
}) => {  
  const [selectedRow, setSelectedRow] = useState<IProject>();
  const [paging, setPaging] = useState<IPaging>({ page: 0, pageSize: 5 });
  const [quickSearch, setQuickSearch] = useState<string>("");
  const [sort, setSort] = useState<ISorting>({ column: "", order: "asc" });
  const [filter, setFilter] = useState<IFilter>({});
  const pageRef = useRef();
  const tableRef = createRef<any>();
  const dispatch = useAppDispatch();

  const lastFocusedInputIndex = useRef<number | null>(null);

  const columns = useMemo(() => [
    {
      title: t("external-controls-project-number"),
      field: "number",
      sortingName: "PROJECT_NUMBER",
    },
    {
      title: t("external-controls-beneficiary-name"),
      field: "beneficiaryName",
      sortingName: "BENEFICIARY_NAME",
    },
    {
      title: t("external-controls-beneficiary-id"),
      field: "beneficiaryIdentityNumber",
      sortingName: "BENEFICIARY_IDENTITY_NUMBER",
    },
    {
      title: t("external-controls-project-title"),
      field: "title",
      sortingName: "TITLE",
    },
  ], []);

  useEffect(() => {
    fetchProjectsData(paging, sort, filter, quickSearch);

    if (tableRef.current && tableRef.current.dataManager) {
      tableRef.current.dataManager.columns.forEach((item: any) => {
        if (tableRef.current) {
          tableRef.current.onFilterChange(item.tableData.id, "");
        }
      });
    }

    return () => {
      setFilter({});
      setQuickSearch("");
      setSort({ column: "", order: "asc" });
      setPaging({ page: 0, pageSize: 5 });
    };
  }, []);

  const prepareData = (data: []) => {
    return [...data];
  };

  const projectsStore = store.getState().projects;

  const fetchProjectsData = (
    paging: IPaging,
    sort: ISorting,
    filter: IFilter,
    quickSearch?: string
  ) => {
    dispatch(
      getPagedProjects(
        paging.page,
        paging.pageSize,
        sort.column,
        sort.order,
        filter.projectNumber,
        filter.beneficiaryName,
        filter.beneficiaryIdentityNumber,
        filter.title,
        quickSearch
      )
    );
  };

  const handleQuickSearchChange = (search: string) => {
    setQuickSearch(search);
    fetchProjectsData(paging, sort, filter, search);
  };

  const handleFilterChange = (filter: any) => {
    const inputs = Array.from(document.getElementsByClassName("MuiInput-inputTypeSearch")) as HTMLInputElement[];
    lastFocusedInputIndex.current = inputs.indexOf(document.activeElement as HTMLInputElement);

    const keyValuePairs = filter.map((f: any) => Object.values(f));

    const filters = keyValuePairs
      .filter(
        ([property, value]: [string, any]) =>
          property && value !== undefined && value !== ""
      )
      .map(([property, value]: [string, any]) => ({
        property: property,
        value,
        condition: "Contains",
      }));

    const newFilter = {
      projectNumber: filters.find((f: any) => f.property === "number")?.value,
      beneficiaryName: filters.find(
        (f: any) => f.property === "beneficiaryName"
      )?.value,
      beneficiaryIdentityNumber: filters.find(
        (f: any) => f.property === "beneficiaryIdentityNumber"
      )?.value,
      title: filters.find((f: any) => f.property === "title")?.value,
    };

    setFilter(newFilter);

    fetchProjectsData(paging, sort, newFilter, quickSearch);
  };

  const findColumnIndexBySortingName = (sortingName: string) => {
    if (!sortingName) {
      return "";
    }

    return columns.findIndex((column) => column.sortingName === sortingName);
  };

  useEffect(() => {
    if (projectsStore.isLoading || lastFocusedInputIndex.current === null) return;
    const index = lastFocusedInputIndex.current;
    setTimeout(() => {
      const inputs = Array.from(document.getElementsByClassName("MuiInput-inputTypeSearch")) as HTMLInputElement[];
      inputs[index]?.focus();
      lastFocusedInputIndex.current = null;
    }, 100);
  }, [projectsStore.isLoading]);

  return (
    <>
      {!projectsStore.isLoading ? (
        <TableCustomLazyLoading
          title={<span className="h2">{t('external-controls-control-list')}</span>}
          columns={columns}
          data={prepareData(projectsStore.projects)}
          page={paging.page}
          pageSize={paging.pageSize}
          initialPage={0}
          count={projectsStore.totalElements}
          parentRef={pageRef}
          orderBy={findColumnIndexBySortingName(sort.column)}
          orderDirection={sort.order}
          onFilterChange={handleFilterChange}
          tableRef={tableRef}
          onOrderChange={(column: number, order: string) => {
            const sortingColumn = columns[column];

            let sortingColumnName = "";

            if (sortingColumn) {
              sortingColumnName = sortingColumn.sortingName;
            }

            const newSort: ISorting = { column: sortingColumnName, order };

            setSort(newSort);

            fetchProjectsData(paging, newSort, filter, quickSearch);
          }}
          onChangeRowsPerPage={(pageSize: number) => {
            const newPaging: IPaging = { page: paging.page, pageSize };

            setPaging({ ...newPaging, pageSize });
            fetchProjectsData(newPaging, sort, filter, quickSearch);
          }}
          onChangePage={(page: number, size: number) => {
            const newPaging: IPaging = {
              page,
              pageSize: size,
            };

            setPaging(newPaging);
            fetchProjectsData(newPaging, sort, filter, quickSearch);
          }}
          onQuickSearchChange={(value: string) => {
            handleQuickSearchChange(value);
          }}
          quickSearch={quickSearch}
          verticalAlign={"middle"}
          options={{
            filtering: true,
            showTitle: false,
            paginationType: "stepped",
            pageSizeOptions: [],
            disableDialogOpenAsFocusTrap: true,
            disableMenuOpenAsFocusTrap: true,
            headerStyle: {
              backgroundColor: "#0D47A1",
              color: "white",
              textAlign: "center",
            },
            rowStyle: (rowData: IProject) => {
              return {
                backgroundColor:
                  selectedRow && selectedRow.projectId === rowData.projectId
                    ? "#EEE"
                    : "#FFF",
                overflowWrap: "anywhere",
              };
            },
          }}
          onRowClick={(evt: React.MouseEvent, selectedRow: never) =>
            setSelectedRow(selectedRow)
          }
          localization={{
            pagination: {
              labelDisplayedRows: `{from}-{to} ${t('table-of')} {count}`,
              firstAriaLabel: t("table-first-page"),
              firstTooltip: t("table-first-page"),
              previousAriaLabel: t("table-previous-page"),
              previousTooltip: t("table-previous-page"),
              nextAriaLabel: t("table-next-page"),
              nextTooltip: t("table-next-page"),
              lastAriaLabel: t("table-last-page"),
              lastTooltip: t("table-last-page"),
              labelRowsPerPage: t("table-rows-per-page"),
              labelRowsSelect: t("table-rows"),
            },
            toolbar: {
              nRowsSelected: `{0} ${t('table-row-selected')}`,
              searchTooltip: t("table-search"),
              searchPlaceholder: t("table-search"),
            },
            header: {
              actions: t("table-actions"),
            },
            body: {
              emptyDataSourceMessage: t("table-no-results"),
              filterRow: {
                filterTooltip: t("table-filter"),
              },
            },
          }}
        />
      ) : <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px 0'}}><Waiter  /></div>}

      <div className="btn_group">
        <button
          className="btn save-btn uppercase"
          onClick={() => {
            handleProject(selectedRow);
          }}
        >
          {t('g-select')}
        </button>
        <button
          className="btn cancel-btn uppercase"
          onClick={() => {
            handleClosePopup();
          }}
        >
          {t('g-cancel')}
        </button>
      </div>
    </>
  );
};
